import * as React from "react";
import { useNavigate } from "react-router";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import { BRAND } from "../risikoanalyseStep/tokens";
import { bestaetigen } from "../redesign/bestaetigen";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/* ──────────────────────────────────────────────────────────────────────────
   Types
   ────────────────────────────────────────────────────────────────────────── */

export type RisikoanalyseStartMode = "none" | "kundenmodus" | "beratermodus";

export type ExistingKundenLink = {
  url: string;
  token: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  contactId: number | undefined;
  mode: RisikoanalyseStartMode;
  /** Token of the latest submitted risikoanalyse form (kurz or step). */
  submittedToken: string | null;
  /**
   * Existing, not-yet-submitted Kundenlink for this contact (if any).
   * When present in mode "none" the popup shows the link with Copy /
   * Open / Revoke instead of the "Link erzeugen" action.
   */
  existingKundenLink: ExistingKundenLink | null;
  /** Refresh the parent's form list after generating/sending/revoking a link. */
  onRefresh: () => void;
};

/* ──────────────────────────────────────────────────────────────────────────
   Inline SVG icons (kept local so the popup is self-contained)
   ────────────────────────────────────────────────────────────────────────── */

const ic = (children: JSX.Element) => (
  <svg
    viewBox="0 0 36 36"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: "100%", height: "100%", display: "block" }}
  >
    {children}
  </svg>
);

const Icons = {
  link: () =>
    ic(
      <>
        <path d="M15 21l-3 3a5 5 0 0 1-7-7l4-4a5 5 0 0 1 7 0" />
        <path d="M21 15l3-3a5 5 0 0 1 7 7l-4 4a5 5 0 0 1-7 0" />
      </>
    ),
  user: () =>
    ic(
      <>
        <circle cx="18" cy="13" r="5" />
        <path d="M7 30c0-5 5-9 11-9s11 4 11 9" />
      </>
    ),
  clipboardCheck: () =>
    ic(
      <>
        <rect x="9" y="6" width="18" height="24" rx="2" />
        <rect x="13" y="3" width="10" height="5" rx="1" />
        <polyline points="13,19 17,23 24,15" />
      </>
    ),
  copy: () =>
    ic(
      <>
        <rect x="9" y="3" width="18" height="22" rx="2" />
        <path d="M5 9v22h18" />
      </>
    ),
  close: () =>
    ic(
      <>
        <line x1="9" y1="9" x2="27" y2="27" />
        <line x1="27" y1="9" x2="9" y2="27" />
      </>
    ),
  arrow: () =>
    ic(
      <>
        <line x1="8" y1="18" x2="28" y2="18" />
        <polyline points="22,12 28,18 22,24" />
      </>
    ),
  openExternal: () =>
    ic(
      <>
        <path d="M20 6h10v10" />
        <line x1="14" y1="22" x2="30" y2="6" />
        <path d="M26 20v8H6V8h8" />
      </>
    ),
  trash: () =>
    ic(
      <>
        <line x1="6" y1="10" x2="30" y2="10" />
        <path d="M9 10v20h18V10" />
        <line x1="14" y1="14" x2="14" y2="26" />
        <line x1="22" y1="14" x2="22" y2="26" />
        <path d="M13 10V6h10v4" />
      </>
    ),
};

/* ──────────────────────────────────────────────────────────────────────────
   Styles
   ────────────────────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(20,8,20,0.55)",
    backdropFilter: "blur(2px)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  panel: {
    width: "min(640px, 100%)",
    maxHeight: "min(720px, 92vh)",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 24px 60px rgba(20,8,20,0.32)",
  },
  head: {
    position: "relative",
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "22px",
    paddingRight: "22px",
    color: BRAND.white,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    overflow: "hidden",
    "::after": {
      content: '""',
      position: "absolute",
      right: "-40px",
      bottom: "-40px",
      width: "140px",
      height: "140px",
      backgroundColor: "rgba(247,168,35,0.16)",
      ...shorthands.borderRadius("999px"),
      pointerEvents: "none",
    },
  },
  eyebrow: {
    display: "inline-block",
    fontSize: "0.7rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 700,
    color: BRAND.gold,
    marginBottom: "4px",
    position: "relative",
    zIndex: 1,
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.35rem",
    fontWeight: 600,
    lineHeight: 1.2,
    color: BRAND.white,
    margin: 0,
    position: "relative",
    zIndex: 1,
  },
  subtitle: {
    fontSize: "0.86rem",
    color: "rgba(255,255,255,0.8)",
    lineHeight: 1.4,
    marginTop: "6px",
    position: "relative",
    zIndex: 1,
  },
  closeBtn: {
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "32px",
    height: "32px",
    cursor: "pointer",
    appearance: "none",
    color: BRAND.white,
    backgroundColor: "rgba(255,255,255,0.12)",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    ":hover": { backgroundColor: "rgba(255,255,255,0.22)" },
  },
  closeIconSlot: {
    width: "16px",
    height: "16px",
    display: "inline-flex",
  },
  body: {
    flexGrow: 1,
    overflowY: "auto",
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "22px",
    paddingRight: "22px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
    backgroundColor: BRAND.bg,
    fontFamily: BRAND.fontPrimary,
  },
  actionGrid: {
    display: "grid",
    gap: "12px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 560px)": { gridTemplateColumns: "1fr 1fr" },
  },
  actionGridSingle: {
    "@media (min-width: 560px)": { gridTemplateColumns: "1fr" },
  },
  actionTile: {
    position: "relative",
    cursor: "pointer",
    appearance: "none",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    minHeight: "140px",
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    overflow: "hidden",
    transitionProperty: "transform, box-shadow, border-color",
    transitionDuration: "160ms",
    ...shorthands.border("2px", "solid", BRAND.ink),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 6px 14px rgba(45,20,43,0.18)",
    outlineWidth: "2px",
    outlineStyle: "solid",
    outlineColor: "transparent",
    outlineOffset: "-6px",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px rgba(45,20,43,0.28)",
      outlineColor: BRAND.gold,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.55,
      transform: "none",
      boxShadow: "0 6px 14px rgba(45,20,43,0.18)",
      outlineColor: "transparent",
    },
  },
  actionTileSecondary: {
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px rgba(45,20,43,0.18)",
      ...shorthands.borderColor(BRAND.gold),
      outlineColor: "transparent",
    },
  },
  actionIcon: {
    width: "34px",
    height: "34px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "7px",
    paddingRight: "7px",
    boxSizing: "border-box",
    backgroundColor: "rgba(247,168,35,0.18)",
    color: BRAND.gold,
    ...shorthands.borderRadius("10px"),
    position: "relative",
    zIndex: 1,
  },
  actionIconLight: {
    backgroundColor: "rgba(77,32,81,0.08)",
    color: BRAND.purple,
  },
  actionLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    fontWeight: 600,
    lineHeight: 1.2,
    marginTop: "auto",
    position: "relative",
    zIndex: 1,
  },
  actionHint: {
    fontSize: "0.8rem",
    lineHeight: 1.35,
    color: "rgba(255,255,255,0.8)",
    position: "relative",
    zIndex: 1,
  },
  actionHintLight: {
    color: BRAND.muted,
  },
  actionCta: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.gold,
    marginTop: "6px",
    position: "relative",
    zIndex: 1,
  },
  actionCtaLight: {
    color: BRAND.purple,
  },
  ctaArrowSlot: {
    width: "12px",
    height: "12px",
    display: "inline-flex",
  },
  /* Link generated row */
  linkBoxLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: "4px",
  },
  linkBox: {
    display: "flex",
    columnGap: "8px",
    alignItems: "stretch",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "8px",
  },
  linkInput: {
    flexGrow: 1,
    minWidth: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
    backgroundColor: "transparent",
    ...shorthands.border("0"),
    ":focus": { outline: "none" },
  },
  copyBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      backgroundColor: BRAND.ink,
      ...shorthands.borderColor(BRAND.ink),
    },
  },
  copyIconSlot: {
    width: "14px",
    height: "14px",
    display: "inline-flex",
  },
  linkActionsRow: {
    display: "flex",
    columnGap: "8px",
    marginTop: "8px",
    flexWrap: "wrap",
  },
  linkActionBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "12px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      backgroundColor: BRAND.bg,
      ...shorthands.borderColor(BRAND.muted),
    },
    ":disabled": { cursor: "not-allowed", opacity: 0.55 },
  },
  linkActionDanger: {
    color: "#9b1c1c",
    ...shorthands.borderColor("rgba(220,38,38,0.35)"),
    ":hover": {
      backgroundColor: "rgba(220,38,38,0.06)",
      ...shorthands.borderColor("#9b1c1c"),
    },
  },
  linkHelpText: {
    marginTop: "8px",
    marginBottom: 0,
    fontSize: "0.82rem",
    color: BRAND.muted,
    lineHeight: 1.4,
  },
  bodyText: {
    fontSize: "0.9rem",
    lineHeight: 1.45,
    color: BRAND.ink,
    margin: 0,
  },
  errorBox: {
    color: "#9b1c1c",
    backgroundColor: "rgba(220,38,38,0.08)",
    fontSize: "0.86rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "rgba(220,38,38,0.25)"),
  },
  spinnerRow: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    fontSize: "0.86rem",
    color: BRAND.muted,
  },
  foot: {
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "10px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "22px",
    paddingRight: "22px",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.white,
  },
  ghostBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.86rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      backgroundColor: BRAND.bg,
      ...shorthands.borderColor(BRAND.muted),
    },
  },
});

/* ──────────────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────────────── */

const extractTokenFromUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    const parts = parsed.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  } catch {
    const parts = url.split("/").filter(Boolean);
    return parts[parts.length - 1] ?? null;
  }
};

const RisikoanalyseStartPopup: React.FC<Props> = ({
  open,
  onClose,
  contactId,
  mode,
  submittedToken,
  existingKundenLink,
  onRefresh,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [generating, setGenerating] = React.useState(false);
  const [generatedLink, setGeneratedLink] = React.useState<ExistingKundenLink | null>(null);
  const [revokedLocally, setRevokedLocally] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [hasConflict, setHasConflict] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [navigating, setNavigating] = React.useState(false);
  const [revoking, setRevoking] = React.useState(false);

  // Reset transient state whenever the popup closes.
  React.useEffect(() => {
    if (!open) {
      setGenerating(false);
      setGeneratedLink(null);
      setRevokedLocally(false);
      setError(null);
      setHasConflict(false);
      setCopied(false);
      setNavigating(false);
      setRevoking(false);
    }
  }, [open]);

  // Link to render — freshly generated wins, otherwise the existing one from
  // the parent. After a local revoke we suppress both.
  const visibleLink: ExistingKundenLink | null = revokedLocally
    ? null
    : generatedLink ?? existingKundenLink ?? null;

  // Close on Escape.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  /* ── Header copy by state ────────────────────────────────────────────── */
  const titleByMode: Record<RisikoanalyseStartMode, { eyebrow: string; title: string; subtitle: string }> = {
    none: {
      eyebrow: "Risikoanalyse",
      title: "Wie möchten Sie starten?",
      subtitle:
        "Senden Sie dem Kunden einen Link zum Ausfüllen oder starten Sie direkt in den Beratermodus.",
    },
    kundenmodus: {
      eyebrow: "Risikoanalyse",
      title: "Kundenangaben sind eingegangen",
      subtitle:
        "Werten Sie die Antworten aus, damit die Daten in den Kontakt übernommen werden – oder ergänzen Sie sie im Beratermodus.",
    },
    beratermodus: {
      eyebrow: "Risikoanalyse",
      title: "Risikoanalyse ist abgeschlossen",
      subtitle: "Öffnen Sie die Auswertung, um die erfassten Daten und Empfehlungen einzusehen.",
    },
  };

  const head = titleByMode[mode];

  /* ── Link generation ─────────────────────────────────────────────────── */
  const generateLink = async (): Promise<{ url: string; token: string } | null> => {
    if (!contactId) {
      setError("Kontakt muss zuerst gespeichert werden.");
      return null;
    }
    setGenerating(true);
    setError(null);
    setHasConflict(false);
    try {
      const res = await fetch(apiUrl(`contact/${contactId}/public-form-link`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formType: "risikoanalyse-kurz" }),
      });
      if (!res.ok) {
        if (res.status === 409) {
          let message =
            "Es existiert bereits eine aktive Risikoanalyse für diesen Kontakt.";
          try {
            const data = await res.json();
            if (typeof data?.message === "string") message = data.message;
          } catch {}
          setError(message);
          setHasConflict(true);
          return null;
        }
        throw new Error(`Fehler beim Erzeugen des Links: ${res.status}`);
      }
      const data = await res.json();
      const url: string = data?.url ?? "";
      const token = extractTokenFromUrl(url);
      if (!token) {
        throw new Error("Kein gültiger Token im Backend-Response gefunden.");
      }
      return { url, token };
    } catch (e: any) {
      setError(e?.message ?? "Unbekannter Fehler beim Erzeugen des Links.");
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateKundeLink = async () => {
    const result = await generateLink();
    if (!result) return;
    setGeneratedLink({ url: result.url, token: result.token });
    setRevokedLocally(false);
    onRefresh();
  };

  const handleCopy = async () => {
    if (!visibleLink) return;
    try {
      await navigator.clipboard.writeText(visibleLink.url);
      setCopied(true);
    } catch {
      /* ignore */
    }
  };

  const handleOpen = () => {
    if (!visibleLink) return;
    window.open(visibleLink.url, "_blank", "noopener,noreferrer");
  };

  const handleRevoke = async () => {
    if (!visibleLink || !contactId) return;
    const ok = await bestaetigen({
      titel: "Kundenlink widerrufen?",
      text: "Der Link wird ungültig, und der Kunde kann das Formular nicht mehr aufrufen.",
      ja: "Widerrufen",
      ton: "danger",
    });
    if (!ok) return;
    setRevoking(true);
    setError(null);
    try {
      const res = await fetch(
        apiUrl(`contact/${contactId}/public-form-link/${visibleLink.token}`),
        { method: "DELETE" }
      );
      if (!res.ok) {
        throw new Error(`Fehler beim Widerrufen (Status ${res.status}).`);
      }
      setGeneratedLink(null);
      setRevokedLocally(true);
      setCopied(false);
      onRefresh();
    } catch (e: any) {
      setError(e?.message ?? "Unbekannter Fehler beim Widerrufen des Links.");
    } finally {
      setRevoking(false);
    }
  };

  const goToBeratermodus = () => {
    if (!contactId) return;
    setNavigating(true);
    onClose();
    // Route nach dem Refactor: das große Step-Form ist jetzt eine dedizierte
    // interne Seite mit Sidebar (collapsed). Funktion-Name bleibt aus Legacy-
    // Gründen, alle drei Aufrufer-Buttons landen hier.
    navigate(`/contacts/${contactId}/risikoanalyse-step`);
  };


  /* ── Body content ────────────────────────────────────────────────────── */
  const renderLinkBlock = (isFresh: boolean) => (
    <div>
      <div className={styles.linkBoxLabel}>Kundenlink</div>
      <div className={styles.linkBox}>
        <input
          className={styles.linkInput}
          readOnly
          value={visibleLink!.url}
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          className={styles.copyBtn}
          onClick={handleCopy}
        >
          <span className={styles.copyIconSlot}>
            <Icons.copy />
          </span>
          {copied ? "Kopiert" : "Kopieren"}
        </button>
      </div>
      <div className={styles.linkActionsRow}>
        <button
          type="button"
          className={styles.linkActionBtn}
          onClick={handleOpen}
          disabled={revoking}
        >
          <span className={styles.copyIconSlot}>
            <Icons.openExternal />
          </span>
          Öffnen
        </button>
        <button
          type="button"
          className={mergeClasses(styles.linkActionBtn, styles.linkActionDanger)}
          onClick={handleRevoke}
          disabled={revoking}
        >
          <span className={styles.copyIconSlot}>
            <Icons.trash />
          </span>
          {revoking ? "Widerrufe…" : "Widerrufen"}
        </button>
      </div>
      <p className={styles.linkHelpText}>
        {isFresh
          ? "Link erstellt. Senden Sie ihn an den Kunden – die Antworten erscheinen anschließend hier in der Risiken-Übersicht."
          : "Es existiert bereits ein Kundenlink für diesen Kontakt. Sie können ihn kopieren, öffnen oder widerrufen."}
      </p>
    </div>
  );

  const renderActions = () => {
    if (mode === "none") {
    // No risikoanalyse has been submitted yet. Show either the existing
    // link (with Open/Revoke) or the two action tiles.
    if (visibleLink) {
      return (
        <>
          {renderLinkBlock(!!generatedLink)}
          <div className={mergeClasses(styles.actionGrid, styles.actionGridSingle)}>
            <button
              type="button"
              className={mergeClasses(styles.actionTile, styles.actionTileSecondary)}
              onClick={goToBeratermodus}
              disabled={navigating || !contactId}
            >
              <span className={mergeClasses(styles.actionIcon, styles.actionIconLight)}>
                <Icons.user />
              </span>
              <span className={styles.actionLabel}>In den Beratermodus</span>
              <span className={mergeClasses(styles.actionHint, styles.actionHintLight)}>
                Sie führen die Risikoanalyse direkt selbst – ohne Kundenlink.
              </span>
              <span className={mergeClasses(styles.actionCta, styles.actionCtaLight)}>
                Beratermodus starten
                <span className={styles.ctaArrowSlot}>
                  <Icons.arrow />
                </span>
              </span>
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <p className={styles.bodyText}>
          Wählen Sie eine Option, um die Risikoanalyse zu beginnen.
        </p>
        <div className={styles.actionGrid}>
          <button
            type="button"
            className={styles.actionTile}
            onClick={handleCreateKundeLink}
            disabled={generating || !contactId}
          >
            <span className={styles.actionIcon}>
              <Icons.link />
            </span>
            <span className={styles.actionLabel}>Link für den Kunden erstellen</span>
            <span className={styles.actionHint}>
              URL erzeugen, an den Kunden senden und das ausgefüllte Formular
              hier prüfen.
            </span>
            <span className={styles.actionCta}>
              Link erzeugen
              <span className={styles.ctaArrowSlot}>
                <Icons.arrow />
              </span>
            </span>
          </button>
          <button
            type="button"
            className={styles.actionTile}
            onClick={goToBeratermodus}
            disabled={generating || navigating || !contactId}
          >
            <span className={styles.actionIcon}>
              <Icons.user />
            </span>
            <span className={styles.actionLabel}>In den Beratermodus</span>
            <span className={styles.actionHint}>
              Sie führen die Risikoanalyse direkt selbst – ohne Kundenlink.
            </span>
            <span className={styles.actionCta}>
              Beratermodus starten
              <span className={styles.ctaArrowSlot}>
                <Icons.arrow />
              </span>
            </span>
          </button>
        </div>
      </>
    );
  }

    if (mode === "kundenmodus") {
      return (
        <>
          <p className={styles.bodyText}>
            Der Kunde hat den Fragebogen ausgefüllt. Seine Antworten sind im
            Beratermodus vorbelegt — dort ergänzen, prüfen und im letzten
            Schritt in den Kontakt übernehmen.
          </p>
          {/*
            * Eine Kachel statt zweier.
            *
            * „Auswertung machen" führte auf eine eigene Seite, die dieselben
            * Felder ein zweites Mal ausbreitete — und nur sie trug den Knopf,
            * der die Angaben wirklich in den Kontakt übernimmt. Beides steckt
            * jetzt im Fragebogen: Die Antworten des Kunden sind dort
            * vorbelegt, und der letzte Schritt übernimmt sie. Zwei Einstiege
            * für dieselbe Arbeit hiessen nur, dass man den falschen erwischen
            * kann.
            */}
          <div className={mergeClasses(styles.actionGrid, styles.actionGridSingle)}>
            <button
              type="button"
              className={styles.actionTile}
              onClick={goToBeratermodus}
              disabled={navigating || !contactId}
            >
              <span className={styles.actionIcon}>
                <Icons.clipboardCheck />
              </span>
              <span className={styles.actionLabel}>Analyse öffnen und auswerten</span>
              <span className={styles.actionHint}>
                Antworten des Kunden ergänzen, prüfen und in den Kontakt
                übernehmen.
              </span>
              <span className={styles.actionCta}>
                Beratermodus öffnen
                <span className={styles.ctaArrowSlot}>
                  <Icons.arrow />
                </span>
              </span>
            </button>
          </div>
        </>
      );
    }

    // mode === "beratermodus"
    return (
      <>
        <p className={styles.bodyText}>
          Die Risikoanalyse ist erfasst. Sie können sie jederzeit wieder öffnen,
          korrigieren und erneut in den Kontakt übernehmen.
        </p>
        <div className={mergeClasses(styles.actionGrid, styles.actionGridSingle)}>
          <button
            type="button"
            className={styles.actionTile}
            onClick={goToBeratermodus}
            // Nicht an `submittedToken` hängen: Der Beratermodus legt seine
            // Daten direkt am Kontakt ab (KEIN PublicFormToken), hier ist der
            // Token per Design immer null — der Knopf wäre dauerhaft ausgegraut.
            disabled={navigating || !contactId}
          >
            <span className={styles.actionIcon}>
              <Icons.clipboardCheck />
            </span>
            <span className={styles.actionLabel}>Analyse öffnen</span>
            <span className={styles.actionHint}>
              Erfasste Angaben, empfohlene Detailanalysen und die Übernahme in
              den Kontakt — alles im letzten Schritt der Analyse.
            </span>
            <span className={styles.actionCta}>
              Beratermodus öffnen
              <span className={styles.ctaArrowSlot}>
                <Icons.arrow />
              </span>
            </span>
          </button>
        </div>
      </>
    );
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Risikoanalyse starten"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.panel}>
        <div className={styles.head}>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Schließen"
          >
            <span className={styles.closeIconSlot}>
              <Icons.close />
            </span>
          </button>
          <span className={styles.eyebrow}>{head.eyebrow}</span>
          <h2 className={styles.title}>{head.title}</h2>
          <p className={styles.subtitle}>{head.subtitle}</p>
        </div>

        <div className={styles.body}>
          {renderActions()}
          {generating && (
            <div className={styles.spinnerRow}>Erzeuge Link…</div>
          )}
          {error && <div className={styles.errorBox}>{error}</div>}
        </div>

        <div className={styles.foot}>
          {visibleLink || hasConflict ? (
            <button type="button" className={styles.ghostBtn} onClick={onClose}>
              Schließen
            </button>
          ) : (
            <button type="button" className={styles.ghostBtn} onClick={onClose}>
              Abbrechen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RisikoanalyseStartPopup;
